/**
 * Review queue API (TMMAPP-0011, design §9.4; summary TMMAPP-0031; payee proposals
 * TMMAPP-0032) — `GET /v1/me/review` and `GET /v1/me/review/summary`. Wire types from
 * `src/api/generated/`; normalized into the module's domain type. Every anchor but the account is
 * optional. An unknown `kind` drops the item; an unknown `source` reads as null.
 */
import { request } from './client';
import type {
  MissingAccountDismissRequest,
  PayeeRef as WirePayeeRef,
  PayeeSuggestionSampleLine as WireSampleLine,
  ReviewItem as WireReviewItem,
  ReviewListResponse as WireReviewList,
  ReviewSummaryResponse as WireReviewSummary,
} from './generated';
import { normalizeNamingSource, type NamingSource } from './namingSource';
import type { PayeeRef } from './payees';

export type ReviewKind =
  | 'MISSED'
  | 'CONFIRM_MATCH'
  | 'REPLACE_SYNTHETIC'
  | 'PLAN_PARTIAL'
  | 'RULE_PARTIAL'
  | 'AI_MATCH_PROPOSAL'
  | 'AI_MATCH_APPLIED'
  | 'AI_CATEGORY_APPLIED'
  | 'AI_PAYEE_PROPOSAL'
  | 'TRANSFER_CANDIDATE'
  | 'TRANSFER_SUGGESTION'
  | 'MISSING_ACCOUNT'
  | 'PARTIAL_OVERDUE';

/** TMMAPI-0157's provenance (TMM-B9); `MEMORY` is payee memory, never AI. */
export type ReviewItemSource = 'DETERMINISTIC' | 'MEMORY' | 'AI';

/** Why a missing-account item was raised (TMMAPI-0165). Unknown values are dropped. */
export type MissingAccountEvidence = 'SELF_NAME' | 'OWN_TRANSFER_PHRASE';

export interface ReviewSampleLine {
  date: string | null;
  payee: string | null;
  amountMinor: number | null;
  accountName: string | null;
}

export interface ReviewItem {
  kind: ReviewKind;
  accountId: number;
  seriesId: number | null;
  originalOn: string | null;
  transactionId: number | null;
  candidateTransactionIds: number[];
  sliceRef: string | null;
  envelopeId: number | null;
  allocationId: number | null;
  proposalId: number | null;
  confidence: number | null;
  payee: string | null;
  payeeRef: PayeeRef | null;
  /** Present on AI_PAYEE_PROPOSAL rows. */
  suggestionId: number | null;
  /**
   * The bank text's key. On an identity-keyed `AI_PAYEE_PROPOSAL` it is the counterparty's display
   * name instead (TMMAPI-0170): display only, never sent back as a key.
   */
  payeeNorm: string | null;
  /**
   * On an `AI_PAYEE_PROPOSAL` whose key is a counterparty identity (`ACCT:`, `NAME:`, `ZELLE:` or
   * `XFER:`), that exact key (TMMAPI-0175, owner-only): what "This is my account" sends to
   * `claim-transfer-lines` as `identityKey`. Null otherwise. Never parsed, never rendered.
   */
  identityKey: string | null;
  sampleLine: ReviewSampleLine | null;
  lineCount: number | null;
  /**
   * `MISSING_ACCOUNT` dismissal key (TMMAPI-0157 / 0165): a 4-digit mask, or a wire identity key
   * (`ACCT:` / `NAME:` / `ZELLE:`), sent back as served: the dismissal's `key` and, for an identity
   * key, the claim's `identityKey` (TMMAPI-0170). Never rendered.
   */
  mask: string | null;
  firstOn: string | null;
  lastOn: string | null;
  /** Served on a `MISSING_ACCOUNT` item (TMMAPI-0165); null on every other kind and when absent. */
  identifierKind: string | null;
  identifier: string | null;
  identifierDisplay: string | null;
  /** The bank's name (TMMAPI-0170); its BIC or routing id is `counterpartyBankId`. */
  counterpartyBank: string | null;
  counterpartyBankId: string | null;
  evidence: MissingAccountEvidence | null;
  proposedName: string | null;
  proposedPayee: PayeeRef | null;
  reason: string | null;
  /**
   * The served provenance on every review item (TMMAPI-0157, TMM-B9); null when absent or unknown.
   * Never inferred from the kind.
   */
  source: ReviewItemSource | null;
  /**
   * On an `AI_PAYEE_PROPOSAL`, how its proposed name was made (TMMAPI-0193 U3): `BANK_TEXT`,
   * `KNOWN_MERCHANT` or `AI`. Absent on every other kind, and when absent or unknown.
   */
  namingSource?: NamingSource;
}

/** One of the caller's own accounts with something to review (TMMAPI-0188 U3 item 21b). */
export interface ReviewAccountCount {
  accountId: number;
  total: number;
}

export interface ReviewSummary {
  counts: Record<string, number>;
  /** Includes `REIMBURSEMENT_PROPOSAL` (and thus `reimbursementProposals`) — do not add twice. */
  total: number;
  since: string;
  reimbursementProposals: number;
  /**
   * Each own account's own count, only those with at least one item. Payee proposals are
   * owner-level, so these never sum to `total`: show each account's count, never derive one.
   */
  byAccount: ReviewAccountCount[];
}

const KINDS: ReadonlySet<string> = new Set([
  'MISSED',
  'CONFIRM_MATCH',
  'REPLACE_SYNTHETIC',
  'PLAN_PARTIAL',
  'RULE_PARTIAL',
  'AI_MATCH_PROPOSAL',
  'AI_MATCH_APPLIED',
  'AI_CATEGORY_APPLIED',
  'AI_PAYEE_PROPOSAL',
  'TRANSFER_CANDIDATE',
  'TRANSFER_SUGGESTION',
  'MISSING_ACCOUNT',
  'PARTIAL_OVERDUE',
]);

const SOURCES: ReadonlySet<string> = new Set(['DETERMINISTIC', 'MEMORY', 'AI']);
const EVIDENCE: ReadonlySet<string> = new Set(['SELF_NAME', 'OWN_TRANSFER_PHRASE']);

function normalizeSampleLine(w: WireSampleLine): ReviewSampleLine {
  return {
    date: w.date ?? null,
    payee: w.payee ?? null,
    amountMinor: w.amountMinor ?? null,
    accountName: w.accountName ?? null,
  };
}

function normalizePayeeRef(w: WirePayeeRef | null | undefined): PayeeRef | null {
  return w == null ? null : { id: w.id, name: w.name };
}

function namingSourceOf(value: string | undefined): { namingSource?: NamingSource } {
  const namingSource = normalizeNamingSource(value);
  return namingSource === null ? {} : { namingSource };
}

/** A review item; `null` (dropped) for a kind this build doesn't know. */
export function normalizeReviewItem(w: WireReviewItem): ReviewItem | null {
  if (!KINDS.has(w.kind)) return null;
  return {
    kind: w.kind,
    accountId: w.accountId,
    seriesId: w.seriesId ?? null,
    originalOn: w.originalOn ?? null,
    transactionId: w.transactionId ?? null,
    candidateTransactionIds: w.candidateTransactionIds ?? [],
    sliceRef: w.sliceRef ?? null,
    envelopeId: w.envelopeId ?? null,
    allocationId: w.allocationId ?? null,
    proposalId: w.proposalId ?? null,
    confidence: w.confidence ?? null,
    payee: w.payee ?? null,
    payeeRef: normalizePayeeRef(w.payeeRef),
    suggestionId: w.suggestionId ?? null,
    payeeNorm: w.payeeNorm ?? null,
    identityKey: w.identityKey != null && w.identityKey.length > 0 ? w.identityKey : null,
    sampleLine: w.sampleLine == null ? null : normalizeSampleLine(w.sampleLine),
    lineCount: w.lineCount ?? null,
    mask: w.mask ?? null,
    firstOn: w.firstOn ?? null,
    lastOn: w.lastOn ?? null,
    identifierKind: w.identifierKind ?? null,
    identifier: w.identifier ?? null,
    identifierDisplay: w.identifierDisplay ?? null,
    counterpartyBank: w.counterpartyBank ?? null,
    counterpartyBankId: w.counterpartyBankId ?? null,
    evidence:
      w.evidence !== undefined && EVIDENCE.has(w.evidence)
        ? (w.evidence as MissingAccountEvidence)
        : null,
    proposedName: w.proposedName ?? null,
    proposedPayee: normalizePayeeRef(w.proposedPayee),
    reason: w.reason ?? null,
    source: SOURCES.has(w.source) ? w.source : null,
    ...namingSourceOf(w.namingSource),
  };
}

export function normalizeReviewList(w: WireReviewList): ReviewItem[] {
  return w.items.flatMap((row) => {
    const item = normalizeReviewItem(row);
    return item ? [item] : [];
  });
}

export function normalizeReviewSummary(w: WireReviewSummary): ReviewSummary {
  return {
    counts: { ...w.counts },
    total: w.total,
    since: w.since,
    reimbursementProposals: w.reimbursementProposals,
    byAccount: w.byAccount.flatMap((row) =>
      row.total > 0 ? [{ accountId: row.accountId, total: row.total }] : [],
    ),
  };
}

export const reviewApi = {
  /** GET /v1/me/review?kind= */
  async list(kind?: ReviewKind): Promise<ReviewItem[]> {
    return normalizeReviewList(
      await request<WireReviewList>('GET', '/v1/me/review', {
        query: kind !== undefined ? { kind } : {},
      }),
    );
  },
  /** GET /v1/me/review/summary — counts only; never use the list for a badge/prompt. */
  async summary(): Promise<ReviewSummary> {
    return normalizeReviewSummary(await request<WireReviewSummary>('GET', '/v1/me/review/summary'));
  },
  /** POST /v1/me/review/{kind}/{id}/dismiss — no body. */
  async dismiss(kind: ReviewKind, id: number): Promise<void> {
    await request<void>('POST', `/v1/me/review/${kind}/${id}/dismiss`);
  },
  /**
   * POST /v1/me/review/MISSING_ACCOUNT/dismiss `{key}` — Dismiss (TMMAPI-0170's body form, which
   * the contract prefers over the path form). `key` is the item's `mask` as served: four digits,
   * or an identity key (`ACCT:` / `NAME:` / `ZELLE:`). Idempotent.
   */
  async dismissMissingAccount(key: string): Promise<void> {
    const json: MissingAccountDismissRequest = { key };
    await request<void>('POST', '/v1/me/review/MISSING_ACCOUNT/dismiss', { json });
  },
};

/**
 * Whether a review item is model-produced (TMM-B9; TMMAPP-0119 item 26): the served `source` alone
 * decides, on every kind. TMMAPI-0157 serves it on every item, so 0121's kind-based fallback is gone.
 */
export function reviewItemIsAi(item: Pick<ReviewItem, 'source'>): boolean {
  return item.source === 'AI';
}
